// src/lib/smsTemplates.js
// 10 outbound SMS templates for the wholesaling cold-text → follow-up flow.
// Placeholders supported:
//   {agentName}        Agent's first name (resolved from profile.name)
//   {ownerFirstName}   Owner's first name (resolved from owners[0].name)
//   {city}             Lead address city
//   {state}            Lead address state
//   {streetName}       Street name without house number, e.g. "Helen St"

export const SMS_TEMPLATES = [
  {
    id: 'intro',
    label: 'Intro',
    description: 'First touch — fresh lead',
    body: "Hello, my name is {agentName}. I saw that you have a house for sale in {city}, {state} and I'm interested in buying it, do you mind if I call to talk about it?"
  },
  {
    id: 'followup_24h',
    label: 'Follow-up · 24h · No response',
    description: 'Light nudge the day after intro',
    body: "Hi {ownerFirstName}, just wanted to make sure my message came through yesterday. Any chance you have a few minutes today to chat about your property on {streetName}?"
  },
  {
    id: 'followup_3d',
    label: 'Follow-up · 3 days',
    description: '3 days after, slightly warmer',
    body: "Hey {ownerFirstName}, circling back on the {streetName} property. I'm still really interested — would a quick call later this week work for you?"
  },
  {
    id: 'followup_1w',
    label: 'Follow-up · 1 week',
    description: 'A week later — softer, longer leash',
    body: "Hi {ownerFirstName}, hope your week is going well. Just touching base about your home on {streetName}. Should I follow up again next week or is now a better time?"
  },
  {
    id: 'ask_callback',
    label: 'Ask for a callback',
    description: 'Direct ask for a phone window',
    body: "Hi {ownerFirstName}, when's a good time for a 5-minute call today or tomorrow? Happy to work around your schedule — just let me know what works best."
  },
  {
    id: 'offer_time',
    label: 'Offer two specific times',
    description: 'Concrete options to lower friction',
    body: "Hey {ownerFirstName}, I have time around 2pm or 5pm today to talk through your house on {streetName}. Either of those work for you?"
  },
  {
    id: 'voicemail_follow',
    label: 'After voicemail',
    description: 'Leave-a-text after leaving voicemail',
    body: "Hi {ownerFirstName}, just left you a voicemail. When you get a chance, give me a call back at this number — won't take more than a few minutes."
  },
  {
    id: 'negotiation_open',
    label: 'Reopen negotiation',
    description: 'Reignite a stalled conversation',
    body: "Hi {ownerFirstName}, been thinking about your place. Curious if there's any flexibility on price — I'd love to put something workable together for you."
  },
  {
    id: 'thanks_consider',
    label: 'Thanks · post-call',
    description: 'After the first real call',
    body: "Thanks for the call, {ownerFirstName}. I'll follow up later this week with next steps. In the meantime, any questions, just text me here."
  },
  {
    id: 'last_attempt',
    label: 'Last attempt',
    description: 'Polite final reach-out',
    body: "Hi {ownerFirstName}, just one last time — totally understand if the timing isn't right. If anything changes with the {streetName} property, you've got my number."
  },
  {
    id: 'buyers_portfolio_intro',
    label: 'Buyers portfolio intro',
    description: 'Cold text used by the daily 9:13 AM batch — investor angle',
    body: "I'm an investor building my buyers portfolio and I'm buying property in the area, wanted to know if you would consider a cash offer for {address}."
  }
];

// Intro-SMS choices shown in the new-lead confirm popup. The agent picks
// one of these (or "Custom — type your own"), edits the text, verifies the
// city/state are correct, then sends. {city}/{state} are interpolated when
// known; when they're not (phone-first flow) they fall back to bracketed
// [city]/[state] placeholders the agent must fill in before sending.
export const INTRO_SMS_OPTIONS = [
  {
    id: 'property_for_sale',
    label: 'Property for sale',
    body: "Hello, my name is {agentName}. I saw that you have a property for sale in {city}, {state} and I have someone interested in buying it, do you mind if I call to talk about it?"
  },
  {
    id: 'land_for_sale',
    label: 'Land for sale',
    body: "Hello, my name is {agentName}. I saw that you have land for sale in {city}, {state} and I have someone interested in buying it, do you mind if I call to talk about it?"
  },
  {
    id: 'property_located',
    label: 'Property located in…',
    body: "Hello, my name is {agentName}. I see you have a property located in {city}, {state} — are you looking to sell by any chance?"
  },
  {
    id: 'land_located',
    label: 'Land located in…',
    body: "Hello, my name is {agentName}. I see you have land located in {city}, {state} — are you looking to sell by any chance?"
  }
];

// Like interpolateTemplate but leaves an obvious [city]/[state] placeholder
// when the value is unknown, so the agent is prompted to fill it in rather
// than sending "in your area, ". Used by the intro-SMS confirm popup.
export function interpolateIntro(body, { agentName, city, state }) {
  return (body || '')
    .replace(/\{agentName\}/g, agentName || 'your agent')
    .replace(/\{city\}/g, (city || '').trim() || '[city]')
    .replace(/\{state\}/g, (state || '').trim() || '[state]');
}

export function interpolateTemplate(body, ctx) {
  return (body || '')
    .replace(/\{agentName\}/g, ctx.agentName || 'your agent')
    .replace(/\{ownerFirstName\}/g, ctx.ownerFirstName || 'there')
    .replace(/\{city\}/g, ctx.city || 'your area')
    .replace(/\{state\}/g, ctx.state || '')
    .replace(/\{streetName\}/g, ctx.streetName || 'your property')
    .replace(/\{address\}/g, ctx.address || 'your property');
}

// Pull a sensible first name out of "Jane M. Doe" → "Jane".
// Falls back to "there" so the message still reads.
export function ownerFirstName(owners) {
  const full = (owners?.[0]?.name || '').trim();
  if (!full) return '';
  const parts = full.split(/\s+/);
  for (const p of parts) {
    if (p.length > 1 && !p.endsWith('.')) return p;
  }
  return parts[0] || '';
}

// "537 Helen St" → "Helen St" so messages don't sound like a robot reading a map.
export function streetNameOnly(line1) {
  if (!line1) return '';
  return String(line1).trim().replace(/^\d+\s+/, '');
}
